import {
  ChimeSDKMessagingClient,
  GetMessagingSessionEndpointCommand,
  ListChannelMessagesCommand,
  SendChannelMessageCommand,
  UpdateChannelMessageCommand,
  UpdateChannelReadMarkerCommand,
} from "@aws-sdk/client-chime-sdk-messaging";
import {
  ConsoleLogger,
  DefaultMessagingSession,
  DefaultWebSocketAdapter,
  LogLevel,
  MessagingSessionConfiguration,
} from "amazon-chime-sdk-js";
import { useEffect, useRef, useState, useCallback } from "react";
import { getChimeMessagingClient, clearChimeMessagingClientCache } from "../Chime/helpers/get-chime-messaging-client";

interface UseChimeMessagingProps {
  channelArn?: string;
  userArn?: string;
}

export function useChimeMessaging({
  channelArn,
  userArn,
}: UseChimeMessagingProps) {
  const clientRef = useRef<ChimeSDKMessagingClient | null>(null);
  const sessionRef = useRef<DefaultMessagingSession | null>(null);
  // Track connection state to prevent multiple simultaneous connection attempts
  const isConnectingRef = useRef(false);
  const [tokenExpired, setTokenExpired] = useState<boolean>(false);
  const reconnectAttemptRef = useRef<number>(0);
  const maxReconnectAttempts = 3;

  const [isConnected, setIsConnected] = useState(false);
  const [loading, setLoading] = useState(true);

  /* ----------------------------------
   * Token expiration helpers
   * ---------------------------------- */
  const isTokenExpiredError = (error: any): boolean => {
    const errorMessage = error?.message || error?.toString() || '';
    const errorCode = error?.code || error?.Code || '';
    console.log("[Chime Hook] Checking if error is token expiration:", { errorMessage, errorCode });
    return (
      errorMessage.toLowerCase().includes('security token') ||
      errorMessage.toLowerCase().includes('token is expired') ||
      errorMessage.toLowerCase().includes('expired') ||
      errorCode === 'ExpiredToken' ||
      errorCode === 'ExpiredTokenException' ||
      (error?.statusCode === 403 && errorMessage.toLowerCase().includes('token'))
    );
  };

  const refreshChimeCredentials = async (): Promise<boolean> => {
    try {
      console.log('[Chime Hook] Refreshing AWS credentials (clearing cache)...');
      clearChimeMessagingClientCache();
      return true;
    } catch (err) {
      console.error('[Chime Hook] Failed to refresh credentials:', err);
      return false;
    }
  };

  const handleTokenExpiration = async () => {
    console.warn('[Chime Hook] AWS token expired, attempting to refresh...');
    
    if (tokenExpired) {
      console.log('[Chime Hook] Token refresh already in progress');
      return;
    }
    
    setTokenExpired(true);
    
    if (sessionRef.current) {
      try {
        sessionRef.current.stop();
      } catch (e) {
        console.warn('[Chime Hook] Error stopping session:', e);
      }
      sessionRef.current = null;
      setIsConnected(false);
    }
    
    await refreshChimeCredentials();
  };

  const retryOnTokenExpiration = async <T,>(
    operation: () => Promise<T>,
    operationName: string,
    retryCount: number = 0
  ): Promise<T | null> => {
    try {
      return await operation();
    } catch (err: any) {
      console.error(`[Chime Hook] Error in ${operationName}:`, err);

      if (isTokenExpiredError(err)) {
        if (retryCount < maxReconnectAttempts) {
          console.log(`[Chime Hook] Token expired in ${operationName}, attempting refresh (attempt ${retryCount + 1}/${maxReconnectAttempts})`);
          reconnectAttemptRef.current = retryCount + 1;
          await handleTokenExpiration();
          return null;
        } else {
          console.error(`[Chime Hook] Max token refresh attempts reached. Reloading app.`);
          window.location.reload();
          return null;
        }
      }

      throw err;
    }
  };

  /* ----------------------------------
   * Init Chime client
   * ---------------------------------- */
  useEffect(() => {
    const init = async () => {
      try {
        const { messagingClient } = await getChimeMessagingClient();
        console.log("[Debug] Chime messaging client initialized");
        clientRef.current = messagingClient;
      } catch (err) {
        console.error("Failed to init Chime client", err);
      } finally {
        setLoading(false);
      }
    };
    init();
  }, []);

  /* ----------------------------------
   * List messages (history)
   * ---------------------------------- */
  const listMessages = useCallback(
    async ({
      nextToken,
      pageSize = 30,
    }: {
      nextToken?: string;
      pageSize?: number;
    }) => {
      try{
          const { messagingClient } = await getChimeMessagingClient();
          console.log("[Debug] Chime messaging client initialized");
          console.log("[Debug] listMessages called", { clientReady: !!messagingClient, channelArn, userArn });
          if (!messagingClient) {
            console.warn("[Debug] Chime client not initialized");
            throw new Error("Chime client not initialized");
          }
          if (!channelArn) {
            console.warn("[Debug] Channel ARN not set");
            throw new Error("Channel ARN not set");
          }
          if (!userArn) {
            console.warn("[Debug] User ARN not set");
            throw new Error("User ARN not set");
          }

          console.log("[Debug] listMessages calling AWS SDK with", { nextToken, pageSize, channelArn });
          
          // Wrap SDK call with retry logic
          const result = await retryOnTokenExpiration(
            async () => await messagingClient.send(
              new ListChannelMessagesCommand({
                ChannelArn: channelArn,
                ChimeBearer: userArn,
                MaxResults: pageSize,
                NextToken: nextToken,
                SortOrder: "DESCENDING",
              })
            ),
            'listMessages',
            reconnectAttemptRef.current
          );
          
          if (!result) {
            // Token expired and refreshing, return empty result
            return { messages: [], nextToken: undefined };
          }
          
          const res = result;

          const messages =
            res.ChannelMessages?.map((m) => {
              const parsedContent = m?.Content ? JSON.parse(m?.Content) : null;
              // Use createdAt from content, ensure it's a number (convert string to number if needed)
              let contentCreatedAt = parsedContent?.createdAt;
              if (typeof contentCreatedAt === 'string') {
                contentCreatedAt = Number(contentCreatedAt);
              }
            return ({
              id: m?.MessageId!,
              content: parsedContent,
              senderArn: m?.Sender?.Arn!,
              createdAt: contentCreatedAt,
            })}) ?? [];
          
          console.log("[Debug] listMessages result - loaded", messages.length, "messages");
          // try {
          //   updateChannelReadMarker();
          // } catch (e) {
          //   console.warn("[Debug] Failed to read marker the messages:", e);
          // }
          return {
            messages,
            nextToken: res.NextToken,
          };
      } catch(err){
        console.error("Failed to list messages", err);
        throw err;
      }
    },
    [channelArn, userArn]
  );

  /* ----------------------------------
   * Send message
   * ---------------------------------- */
  const sendMessage = useCallback(
    async (content: any): Promise<string | undefined> => {
      try{
          const { messagingClient } = await getChimeMessagingClient();
          console.log("[Debug] Chime messaging client initialized");
          console.log("[Debug] sendMessage called", { contentLength: JSON.stringify(content).length, clientReady: !!messagingClient, channelArn, userArn });
          if (!messagingClient) {
            console.warn("[Debug] Chime client not initialized");
            throw new Error("Chime client not initialized");
          }
          if (!channelArn) {
            console.warn("[Debug] Channel ARN not set");
            throw new Error("Channel ARN not set");
          }
          if (!userArn) {
            console.warn("[Debug] User ARN not set");
            throw new Error("User ARN not set");
          }

          console.log("[Debug] sendMessage calling AWS SDK");
          
          // Wrap SDK call with retry logic
          const result = await retryOnTokenExpiration(
            async () => await messagingClient.send(
              new SendChannelMessageCommand({
                ChannelArn: channelArn,
                ChimeBearer: userArn,
                Content: JSON.stringify(content),
                Persistence: "PERSISTENT",
                Type: "STANDARD",
              })
            ),
            'sendMessage',
            reconnectAttemptRef.current
          );
          
          if (!result) {
            // Token expired and refreshing
            return undefined;
          }
          
          console.log("[Debug] sendMessage completed, MessageId:", result.MessageId);
          return result.MessageId; // Return the actual Chime MessageId
      } catch(err){
        console.error("Failed to send message", err);
        throw err;
      }
    },
    [channelArn, userArn]
  );

  const sendSignal = useCallback(
    async (payload: any) => {
      if (!channelArn || !userArn) return;

      const { messagingClient } = await getChimeMessagingClient();
      if (!messagingClient) return;

      await messagingClient.send(
        new SendChannelMessageCommand({
          ChannelArn: channelArn,
          ChimeBearer: userArn,
          Persistence: "NON_PERSISTENT",
          Type: "CONTROL",
          Content: JSON.stringify(payload),
        })
      );
    },
    [channelArn, userArn]
  );

  /* ----------------------------------
   * Update/Edit message
   * ---------------------------------- */
  const updateMessage = useCallback(
    async (messageId: string, content: any) => {
      try {
        const { messagingClient } = await getChimeMessagingClient();
        console.log("[Debug] Chime messaging client initialized for update");
        console.log("[Debug] updateMessage called", { messageId, contentLength: JSON.stringify(content).length, clientReady: !!messagingClient, channelArn, userArn });
        if (!messagingClient) {
          console.warn("[Debug] Chime client not initialized");
          throw new Error("Chime client not initialized");
        }
        if (!channelArn) {
          console.warn("[Debug] Channel ARN not set");
          throw new Error("Channel ARN not set");
        }
        if (!userArn) {
          console.warn("[Debug] User ARN not set");
          throw new Error("User ARN not set");
        }
        if (!messageId) {
          console.warn("[Debug] Message ID not set");
          throw new Error("Message ID not set");
        }

        console.log("[Debug] updateMessage calling AWS SDK");
        
        // Wrap SDK call with retry logic
        await retryOnTokenExpiration(
          async () => await messagingClient.send(
            new UpdateChannelMessageCommand({
              ChannelArn: channelArn,
              MessageId: messageId,
              ChimeBearer: userArn,
              Content: JSON.stringify(content),
            })
          ),
          'updateMessage',
          reconnectAttemptRef.current
        );
        
        console.log("[Debug] updateMessage completed");
      } catch (err) {
        console.error("Failed to update message", err);
        throw err;
      }
    },
    [channelArn, userArn]
  );

  /* ----------------------------------
   * Realtime connection
   * ---------------------------------- */
  const connectRealtime = useCallback(async (onMessage: (msg: any) => void) => {
    // Prevent multiple simultaneous connection attempts
    if (isConnectingRef.current) {
      console.log("[Debug] Connection already in progress, skipping...");
      return;
    }
    
    // If already connected, don't create a new connection
    if (sessionRef.current && isConnected) {
      console.log("[Debug] Already connected to realtime, skipping...");
      return;
    }

    const { messagingClient } = await getChimeMessagingClient();
    console.log("[Debug] Chime messaging client initialized");
    if (!messagingClient || !userArn) return;

    isConnectingRef.current = true;
    
    try {
      // Disconnect any existing session first
      if (sessionRef.current) {
        console.log("[Debug] Stopping existing session before creating new one");
        try {
          sessionRef.current.stop();
        } catch (e) {
          console.warn("[Debug] Error stopping existing session:", e);
        }
        sessionRef.current = null;
        setIsConnected(false);
        // Small delay to ensure WebSocket is fully closed
        await new Promise(resolve => setTimeout(resolve, 100));
      }

    // Handle token expiration when getting the messaging endpoint
    const endpoint = await retryOnTokenExpiration(
      async () => await messagingClient.send(
        new GetMessagingSessionEndpointCommand({})
      ),
      'getMessagingSessionEndpoint',
      reconnectAttemptRef.current
    );

    console.log("[Debug] Retrieved messaging endpoint:", endpoint);
    if (!endpoint || !endpoint.Endpoint?.Url) {
      console.warn("[Chime Hook] Missing or invalid messaging endpoint (token may have expired and not refreshed)");
      isConnectingRef.current = false;
      return;
    }
    console.log("[Debug] Retrieved messaging endpoint:", endpoint);
    console.log("[Debug] Before Chime messaging endpoint", endpoint.Endpoint.Url);
    console.log("[Debug] After Chime messaging endpoint", endpoint.Endpoint.Url);

    const logger = new ConsoleLogger("ChimeMessaging", LogLevel.INFO);
    const wsAdapter = new DefaultWebSocketAdapter(logger);

    const config = new MessagingSessionConfiguration(
      userArn,
      null,
      endpoint?.Endpoint?.Url,
      messagingClient
    );

    const session = new DefaultMessagingSession(
      config,
      logger,
      wsAdapter
    );

    session.addObserver({
        messagingSessionDidStart: () => {
          console.log("[Debug] Messaging session started");
          setIsConnected(true);
          isConnectingRef.current = false;
        },
        messagingSessionDidStop: () => {
          console.log("[Debug] Messaging session stopped");
          setIsConnected(false);
          isConnectingRef.current = false;
        },
        messagingSessionDidReceiveMessage: (raw) => {
          console.log("[Debug] Chime message received", raw);
          const payload = JSON.parse(raw ? raw?.payload : '');
          console.log("[Debug] Chime message payload", payload);
          // Pass the full message info including channelArn for filtering
          onMessage({
            content: payload?.Content ? JSON.parse(payload?.Content) : null,
            channelArn: payload?.ChannelArn || null,
            messageId: payload?.MessageId || null,
            senderArn: payload?.Sender?.Arn || null,
            type: raw?.type || null,
          });
        },
      });

    await session.start();
    sessionRef.current = session;
    } catch (err) {
      console.error("[Debug] Failed to connect realtime:", err);
      isConnectingRef.current = false;
      throw err;
    }
  }, [userArn, isConnected]);

  const disconnectRealtime = useCallback(() => {
    console.log("[Debug] Disconnecting realtime session");
    isConnectingRef.current = false;
    if (sessionRef.current) {
      try {
        sessionRef.current.stop();
      } catch (e) {
        console.warn("[Debug] Error stopping session:", e);
      }
      sessionRef.current = null;
    }
    setIsConnected(false);
  }, []);

  /* ----------------------------------
   * Update channel read marker
   * Updates the timestamp marking where user has read up to
   * ---------------------------------- */
  const updateChannelReadMarker = useCallback(async (channelArn:string) => {
    try {
      const { messagingClient } = await getChimeMessagingClient();
      console.log("[Debug] updateChannelReadMarker called", { clientReady: !!messagingClient, userArn });
      
      if (!messagingClient) {
        console.warn("[Debug] Chime client not initialized");
        throw new Error("Chime client not initialized");
      }
      if (!channelArn) {
        console.warn("[Debug] Channel ARN not set");
        throw new Error("Channel ARN not set");
      }
      if (!userArn) {
        console.warn("[Debug] User ARN not set");
        throw new Error("User ARN not set");
      }

      console.log("[Debug] Updating read marker for channel");
      
      // Wrap SDK call with retry logic
      await retryOnTokenExpiration(
        async () => await messagingClient.send(
          new UpdateChannelReadMarkerCommand({
            ChannelArn: channelArn,
            ChimeBearer: userArn,
          })
        ),
        'updateChannelReadMarker',
        reconnectAttemptRef.current
      );
      
      console.log("[Debug] Read marker updated successfully");
    } catch (err) {
      console.error("[Debug] Failed to update read marker:", err);
      throw err;
    }
  }, [channelArn, userArn]);

  return {
    loading,
    isConnected,
    listMessages,
    sendMessage,
    sendSignal,
    updateMessage,
    connectRealtime,
    disconnectRealtime,
    updateChannelReadMarker
  };
}
